import { LexicalComposer, type InitialConfigType } from '@lexical/react/LexicalComposer';
import { Controller, useFormContext } from 'react-hook-form';
import type { EditorState, LexicalEditor } from 'lexical';

import { PlaygroundNodes } from '@/components/TextEditorWithLexical/nodes';
import { theme } from '@/components/TextEditorWithLexical/themes';

import { SharedHistoryContext, TableContext } from '../TextEditorWithLexical/context';
import { TextEditor } from '../TextEditorWithLexical/TextEditor';

export type RHFTextEditorProps = {
  name: string;
  placeholder: string;
};

// изначально должен иметь хотя бы один узел
export const initialNode = {
  root: {
    children: [
      {
        children: [],
        direction: null,
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: null,
    format: '',
    indent: 0,
    type: 'root',
    version: 1,
  },
};

const createDefaultEditorState = (editor: LexicalEditor) => {
  editor.update(() => {
    const editorState = editor.parseEditorState(JSON.stringify(initialNode));
    editor.setEditorState(editorState);
  });
};

const createSavedEditorState = (editor: LexicalEditor, savedValue: string) => {
  editor.update(() => {
    const editorState = editor.parseEditorState(savedValue);
    editor.setEditorState(editorState);
  });
};

export const RHFTextEditor = (props: RHFTextEditorProps) => {
  const { name, placeholder } = props;
  const { control, getValues } = useFormContext();

  const initialConfig: InitialConfigType = {
    editorState: (editor: LexicalEditor) => {
      const savedValue = getValues(name);
      if (savedValue) {
        createSavedEditorState(editor, savedValue);
      } else {
        createDefaultEditorState(editor);
      }
    },
    namespace: 'Editor',
    nodes: [...PlaygroundNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme,
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => {
        const reassignedOnChange = (editorState: EditorState) => {
          onChange(JSON.stringify(editorState));
        };

        return (
          <LexicalComposer initialConfig={initialConfig}>
            <SharedHistoryContext>
              <TableContext>
                <TextEditor placeholder={placeholder} onChange={reassignedOnChange} />
              </TableContext>
            </SharedHistoryContext>
          </LexicalComposer>
        );
      }}
    />
  );
};
